import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useTableFacetContext,
  useTableFilterQueryParamContext,
} from 'modules/experiments/contexts';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { getExperimentsTableFilterOptions } from 'modules/experiments/ducks/selectors';
import { ExperimentsTableMenuItemOption, TableFilter } from 'modules/experiments/types';
import { decodeMenuId } from 'modules/experiments/utils';
import { BUTTON_SIZES, GhostButton } from 'ui/button';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { WiderMenu } from './Styled';

type TableFilterMap = {
  [k in TableFilter]?: boolean;
};

export const FilterButton = () => {
  const { ct } = useExperimentsTranslations();
  const experimentsTableFilterOptions = useSelector<
    Record<string, any>,
    ExperimentsTableMenuItemOption[]
  >(getExperimentsTableFilterOptions);
  const { selectedFilters, clearSelectedFilters } = useTableFacetContext();
  const { updateQueryParam } = useTableFilterQueryParamContext();
  // Sub Menu Options based on Menu Item Key
  const [menuOptions, setMenuOptions] = useState(experimentsTableFilterOptions);

  /**
   * Update the menu options once the initial redux menu state
   * is updated with the sub options from remote calls.
   *
   * Also, update the hasCountBadge options in the menuOptions.
   * Each time the defaultSelectedOptionIds changes.
   */
  useEffect(() => {
    // A map of all selected filters. We use a map for faster lookup compared to an array.
    const mapSelectedFiltersByFilter = selectedFilters.reduce<TableFilterMap>((acc, menuId) => {
      const { tableFilter } = decodeMenuId(menuId);
      return {
        ...acc,
        [tableFilter]: true,
      };
    }, {});

    const updatedMenuOptions = experimentsTableFilterOptions.map((option) => {
      const hasCountBadge = mapSelectedFiltersByFilter[option.id as TableFilter] || false;
      return {
        ...option,
        hasCountBadge,
      };
    });

    setMenuOptions(updatedMenuOptions);
  }, [experimentsTableFilterOptions, selectedFilters]);

  /**
   * We will not be directly updating the value of the selected filter array.
   * Instead, we will only be updating the url search params.
   * Which in turn will update the selected filter array
   */
  const handleSelectionChange = (selectedId: string) => {
    const { tableFilter, value } = decodeMenuId(selectedId);
    updateQueryParam(tableFilter, value);
  };

  return (
    <>
      {selectedFilters.length > 0 && (
        <GhostButton
          data-test="clear-experiment-filters-button"
          onClick={() => clearSelectedFilters()}
          size={BUTTON_SIZES.LARGE}
        >
          {ct('button.clear')}
        </GhostButton>
      )}
      <WiderMenu
        data-test="experiments-index-filter-button"
        isSelectMenu
        options={menuOptions}
        defaultSelectedOptionIds={selectedFilters}
        handleSelectionChange={handleSelectionChange}
      >
        <FilterMenuTriggerButton selectedFilterCount={selectedFilters.length} />
      </WiderMenu>
    </>
  );
};
